import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../components/ActionMenu/ActionMenuItem';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import CheckboxField from '../../components/Fields/CheckboxField';
import Form from '../../components/Fields/Form';
import SelectListField from '../../components/Fields/SelectListField';
import TextareaField from '../../components/Fields/TextareaField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import WorkflowStepDefinitionModel from '../../models/WorkflowStepDefinitionModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import Step_tab_Actions from './Tabs/Step_tab_Actions';
import Step_tab_Tasks from './Tabs/Step_tab_Tasks';

const WorkflowStepDefinitionView: React.FC = () => {
    const workflows = useSelectListOptions('api/v1/selectlist/WorkflowDefinitions', '-- Select workflow');
    const [serverErrors, setServerErrors] = useState<DataResult<WorkflowStepDefinitionModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const navigate = useNavigate()

    const [formData, setFormData] = useState<WorkflowStepDefinitionModel>();

    let idParams: IdParams = useParams();
    let workflowStepDefinitionId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        workflowStepDefinitionId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (workflowStepDefinitionId !== undefined) {
            DataService.getById<WorkflowStepDefinitionModel>('api/v1/WorkflowStepDefinitions/', workflowStepDefinitionId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [workflowStepDefinitionId]);

    function submit() {
        if (formData) {
            DataService
                .update<WorkflowStepDefinitionModel>(`api/v1/WorkflowStepDefinitions/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    function onFieldChanged(name: string, value: string): void {
        if (formData) {
            formData[name] = value;
            setFormData(formData);
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'title', label: 'Title', validationRules: [{ type: 'isRequired' }] },
            { name: 'subWorkflowDefinitionId', label: 'Sub-workflow' },
            { name: 'isFirstStep', label: 'First step' },
            { name: 'description', label: 'Description' },
        ],
        handleSubmit: submit,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    if (!workflowStepDefinitionId || !formData)
        return <SpinnerOverlay />

    const { isAuthorizedForWorkflowDefinition } = useAuth();
    if (!isAuthorizedForWorkflowDefinition(ApplicationFunctions.ViewWorkflowDefinition, formData.workflowDefinitionId))
        return (<Unauthorized />);

    const formActions =
        <ActionMenu title="Actions">
            <ActionMenuItem width={230} icon="fa fa-gears" title="GoTo workflow definition" onClick={() => navigate('/plm/WorkflowSetup/Workflow/' + formData.workflowDefinitionId)} />
            {formData.subWorkflowDefinitionId !== undefined && <ActionMenuItem width={230} icon="fa fa-gears" title="GoTo sub-workflow definition" onClick={() => navigate('/plm/WorkflowSetup/Workflow/' + formData.subWorkflowDefinitionId)} />}
        </ActionMenu>

    return (
        <div className='main-content'>
            <Form title='Workflow step definition' actions={formActions} footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.UpdateWorkflowDefinition} workflowDefinitionId={formData.workflowDefinitionId}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <TextboxField {...getFieldProps('title')} />
                            <SelectListField {...getFieldProps('subWorkflowDefinitionId')} options={workflows} />
                            <CheckboxField {...getFieldProps('isFirstStep')} />
                            <TextareaField {...getFieldProps('description')} rows={8} />
                        </Col>
                    </Row>
                </Container>
            </Form>
            <Container fluid>
                <Row>
                    <Col md='12'>
                        <HorizontalTabs
                            id='vtabs'
                            defaultActiveKey='tasks'
                            tabs={[
                                {
                                    title: 'Tasks',
                                    eventKey: 'tasks',
                                    component: <Step_tab_Tasks workflowDefinitionId={formData.workflowDefinitionId} workflowStepDefinitionId={workflowStepDefinitionId} ></Step_tab_Tasks>,
                                },
                                {
                                    title: 'Actions',
                                    eventKey: 'actions',
                                    component: <Step_tab_Actions workflowDefinitionId={formData.workflowDefinitionId} workflowStepDefinitionId={workflowStepDefinitionId} ></Step_tab_Actions>,
                                },
                            ]}
                        />
                    </Col>
                </Row>
            </Container>

        </div>
    );
}
export default WorkflowStepDefinitionView;